import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, X, Play, Eye, EyeOff, Home, LayoutDashboard, Monitor, Pin, Clock } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const AdminDisplayWindows = () => {
  const [windows, setWindows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'homepage', 'dashboard'
  const [formData, setFormData] = useState({
    title: '',
    video_url: '',
    description: '',
    is_active: true,
    order: 0,
    location: 'dashboard', // 'dashboard' or 'homepage'
    is_pinned: false,
    auto_rotate_interval: 10
  });

  useEffect(() => {
    fetchWindows();
  }, []);

  const fetchWindows = async () => {
    try {
      const response = await api.get('/admin/display-windows');
      setWindows(response.data);
    } catch (error) {
      console.error('Failed to fetch display windows:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/display-windows/${editingId}`, formData);
        toast.success('Display window updated');
      } else {
        await api.post('/admin/display-windows', formData);
        toast.success('Display window added');
      }
      setShowModal(false);
      resetForm();
      fetchWindows();
    } catch (error) {
      toast.error('Failed to save display window');
    }
  };

  const handleEdit = (window) => {
    setFormData({
      title: window.title,
      video_url: window.video_url,
      description: window.description || '',
      is_active: window.is_active,
      order: window.order || 0,
      location: window.location || 'dashboard',
      is_pinned: window.is_pinned || false,
      auto_rotate_interval: window.auto_rotate_interval || 10
    });
    setEditingId(window.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this display window?')) return;
    try {
      await api.delete(`/admin/display-windows/${id}`);
      toast.success('Display window deleted');
      fetchWindows();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleActive = async (window) => {
    try {
      await api.put(`/admin/display-windows/${window.id}`, {
        ...window,
        is_active: !window.is_active
      });
      toast.success(window.is_active ? 'Window hidden' : 'Window visible');
      fetchWindows();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const togglePin = async (windowItem) => {
    try {
      const response = await api.put(`/admin/display-windows/${windowItem.id}/toggle-pin`);
      toast.success(response.data.message);
      fetchWindows();
    } catch (error) {
      toast.error('Failed to toggle pin');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      video_url: '',
      description: '',
      is_active: true,
      order: 0,
      location: 'dashboard',
      is_pinned: false,
      auto_rotate_interval: 10
    });
    setEditingId(null);
  };

  // Filter windows by location
  const filteredWindows = windows.filter(w => {
    if (activeTab === 'all') return true;
    return w.location === activeTab;
  });

  // Extract YouTube embed URL
  const getEmbedUrl = (url) => {
    if (!url) return '';
    // Handle various YouTube URL formats
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    // If it's already an embed URL or other video URL, return as is
    return url;
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-display-windows-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold">Display Windows</h1>
            <p className="text-muted-foreground mt-1">Manage YouTube videos for Dashboard & Homepage</p>
          </div>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-window-btn"
          >
            <Plus className="w-5 h-5" />
            Add Video
          </button>
        </div>

        {/* Location Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('all')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all ${
              activeTab === 'all' ? 'bg-primary text-white' : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            <Monitor className="w-4 h-4" />
            All ({windows.length})
          </button>
          <button
            onClick={() => setActiveTab('homepage')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all ${
              activeTab === 'homepage' ? 'bg-green-500 text-white' : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            <Home className="w-4 h-4" />
            Homepage ({windows.filter(w => w.location === 'homepage').length})
          </button>
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all ${
              activeTab === 'dashboard' ? 'bg-blue-500 text-white' : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            <LayoutDashboard className="w-4 h-4" />
            Dashboard ({windows.filter(w => w.location === 'dashboard').length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : filteredWindows.length === 0 ? (
          <div className="text-center py-12 glass-card">
            <Play className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              {activeTab === 'all' ? 'No display windows yet. Add your first video!' : `No ${activeTab} videos yet.`}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {filteredWindows.map((w) => (
              <div key={w.id} className={`glass-card overflow-hidden relative ${!w.is_active ? 'opacity-50' : ''}`} data-testid={`window-item-${w.id}`}>
                {/* Pinned indicator */}
                {w.is_pinned && (
                  <div className="absolute top-3 left-3 z-10 flex items-center gap-1 px-2 py-1 bg-yellow-500/90 rounded-full text-xs font-semibold text-black">
                    <Pin className="w-3 h-3" />
                    PINNED
                  </div>
                )}
                <div className="aspect-video bg-black">
                  <iframe
                    src={getEmbedUrl(w.video_url)}
                    title={w.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-lg">{w.title}</h3>
                      {w.description && (
                        <p className="text-sm text-muted-foreground">{w.description}</p>
                      )}
                      {w.location === 'homepage' && (
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          Auto-rotate: {w.auto_rotate_interval || 10}s
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-2 py-1 rounded text-xs ${w.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                        {w.is_active ? 'Active' : 'Hidden'}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${w.location === 'homepage' ? 'bg-green-500/20 text-green-500' : 'bg-blue-500/20 text-blue-500'}`}>
                        {w.location === 'homepage' ? <Home className="w-3 h-3" /> : <LayoutDashboard className="w-3 h-3" />}
                        {w.location === 'homepage' ? 'Homepage' : 'Dashboard'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => toggleActive(w)}
                      className={`p-2 rounded-lg ${w.is_active ? 'bg-yellow-500/20 text-yellow-500' : 'bg-green-500/20 text-green-500'}`}
                      title={w.is_active ? 'Hide' : 'Show'}
                    >
                      {w.is_active ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                    {w.location === 'homepage' && (
                      <button 
                        onClick={() => togglePin(w)} 
                        className={`p-2 rounded-lg ${w.is_pinned ? 'bg-yellow-500/20 text-yellow-500' : 'hover:bg-white/10'}`}
                        title={w.is_pinned ? 'Unpin' : 'Pin to top'}
                      >
                        <Pin className="w-5 h-5" />
                      </button>
                    )}
                    <button onClick={() => handleEdit(w)} className="p-2 hover:bg-white/10 rounded-lg">
                      <Edit2 className="w-5 h-5 text-primary" />
                    </button>
                    <button onClick={() => handleDelete(w.id)} className="p-2 hover:bg-white/10 rounded-lg">
                      <Trash2 className="w-5 h-5 text-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="glass-card p-6 w-full max-w-lg">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">
                  {editingId ? 'Edit Display Window' : 'Add Display Window'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm mb-1">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="Video title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">Video URL *</label>
                  <input
                    type="url"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="https://www.youtube.com/watch?v=... or embed URL"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Paste YouTube video URL or embed URL
                  </p>
                </div>

                <div>
                  <label className="block text-sm mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl resize-none"
                    rows={2}
                    placeholder="Optional description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1">Display Order</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                      min="0"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-5 h-5"
                    />
                    <label htmlFor="is_active">Active (visible)</label>
                  </div>
                </div>

                {/* Location Selector */}
                <div>
                  <label className="block text-sm mb-2">Display Location *</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, location: 'homepage' })}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                        formData.location === 'homepage'
                          ? 'border-green-500 bg-green-500/20 text-green-500'
                          : 'border-white/10 bg-white/5 hover:border-white/20'
                      }`}
                    >
                      <Home className="w-5 h-5" />
                      Homepage
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, location: 'dashboard' })}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                        formData.location === 'dashboard'
                          ? 'border-blue-500 bg-blue-500/20 text-blue-500'
                          : 'border-white/10 bg-white/5 hover:border-white/20'
                      }`}
                    >
                      <LayoutDashboard className="w-5 h-5" />
                      Dashboard
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {formData.location === 'homepage' 
                      ? '✓ Video will be shown on the public homepage' 
                      : '✓ Video will be shown on member dashboard only'}
                  </p>
                </div>

                {/* Homepage-specific settings */}
                {formData.location === 'homepage' && (
                  <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/20">
                    <h4 className="font-medium text-green-500 mb-3 flex items-center gap-2">
                      <Home className="w-4 h-4" />
                      Homepage Settings
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm mb-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Auto-Rotate Interval
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={formData.auto_rotate_interval}
                            onChange={(e) => setFormData({ ...formData, auto_rotate_interval: parseInt(e.target.value) || 10 })}
                            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                            min="0"
                            max="60"
                          />
                          <span className="text-sm text-muted-foreground">sec</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">0 = no auto-rotate</p>
                      </div>
                      <div className="flex items-center gap-2 pt-4">
                        <input
                          type="checkbox"
                          id="is_pinned"
                          checked={formData.is_pinned}
                          onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                          className="w-5 h-5"
                        />
                        <label htmlFor="is_pinned" className="flex items-center gap-1">
                          <Pin className="w-4 h-4 text-yellow-500" />
                          Pin to top
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preview */}
                {formData.video_url && (
                  <div>
                    <label className="block text-sm mb-1">Preview:</label>
                    <div className="aspect-video bg-black rounded-lg overflow-hidden">
                      <iframe
                        src={getEmbedUrl(formData.video_url)}
                        title="Preview"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary">
                    {editingId ? 'Update' : 'Add'} Window
                  </button>
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 bg-white/10 rounded-full">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDisplayWindows;
